import React, { useCallback, useEffect } from 'react';

import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useCreateParityNotification } from '~/hooks/apis/price-parity/useCreateParityNotification';
import { useGetUnsolvedParityNotifications } from '~/hooks/apis/price-parity/useGetUnsolvedParityNotifications';
import { useSendNotificationEmail } from '~/hooks/apis/price-parity/useSendNotificationEmail';
import { useVendorOfferSelection } from '~/hooks/apis/price-parity/useVendorOfferSelection';

import type { NamedEntity, ParityCategory, ParityNotificationBody } from '~/types/services/parityNotifications';

import VendorOfferSelector from '../shared/VendorOfferSelector';
import { genSalesforceId } from '../shared/utils';

import ParityItemsTable from './ParityItemsTable';
import ParityNotificationsGrid from './ParityNotificationsGrid';
import type { FormValues } from './types';

const PARITY_CATEGORIES = [
  { value: 'no_base_rate', label: 'No Base Rate' },
  { value: 'merged_base_rate', label: 'Merged Base Rate' },
  { value: 'missing_base_rate', label: 'Missing Base Rate' },
];

const CATEGORIES_REQUIRING_RATE_PLAN: string[] = ['merged_base_rate', 'missing_base_rate'];

/** Stable fallback so `??` does not allocate a new array each render (useEffect deps). */
const EMPTY_RATE_PLANS: NamedEntity[] = [];

function computeParityUplift(items: FormValues['parityItems']): string | undefined {
  if (items.length === 0) return undefined;
  const totalLe = items.reduce((sum, i) => sum + (i.amount || 0), 0);
  const totalComp = items.reduce((sum, i) => sum + (i.competitor_amount || 0), 0);
  if (totalLe === 0) return undefined;
  const pct = ((totalComp - totalLe) / totalLe) * 100;
  return `${Math.abs(pct).toFixed(1)}%`;
}

function computeParityValue(items: FormValues['parityItems']): string | undefined {
  if (items.length === 0) return undefined;
  const total = items.reduce((sum, i) => sum + Math.abs((i.competitor_amount || 0) - (i.amount || 0)), 0);
  return `$${total.toLocaleString()}`;
}

export default function ParityNotifications() {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      opportunityId: '',
      vendorId: '',
      offerId: '',
      parityCategory: '',
      parityValue: '',
      parityUplift: '',
      ratePlanId: '',
      parityItems: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'parityItems' });
  const vendorIdValue = useWatch({ control, name: 'vendorId' });
  const parityCategoryValue = useWatch({ control, name: 'parityCategory' });
  const ratePlanIdValue = useWatch({ control, name: 'ratePlanId' });
  const needsRatePlan = CATEGORIES_REQUIRING_RATE_PLAN.includes(parityCategoryValue);
  const vendorOfferSelection = useVendorOfferSelection(vendorIdValue);

  const parityRatePlans = vendorOfferSelection.selectionData?.ratePlans ?? EMPTY_RATE_PLANS;

  useEffect(() => {
    if (!needsRatePlan) return;
    if (parityRatePlans.length === 1 && ratePlanIdValue !== parityRatePlans[0].id) {
      setValue('ratePlanId', parityRatePlans[0].id);
      return;
    }
    if (ratePlanIdValue && !parityRatePlans.some((rp) => rp.id === ratePlanIdValue)) {
      setValue('ratePlanId', '');
    }
  }, [needsRatePlan, parityRatePlans, ratePlanIdValue, setValue]);

  const {
    data: notifications = [],
    isLoading: notificationsLoading,
    error: notificationsError,
  } = useGetUnsolvedParityNotifications();

  const { sendEmailMutation, sendingEmailId } = useSendNotificationEmail();

  const createMutation = useCreateParityNotification(() => {
    reset();
    vendorOfferSelection.reset();
  });

  const onSubmit = (data: FormValues) => {
    const body: ParityNotificationBody = {
      opportunityId: data.opportunityId,
      vendorId: data.vendorId,
      offerId: data.offerId,
      parityCategory: data.parityCategory as ParityCategory,
    };
    if (data.parityItems.length > 0) {
      body.parityItems = data.parityItems;
      const uplift = computeParityUplift(data.parityItems);
      if (uplift) body.parityUplift = uplift;
      const value = computeParityValue(data.parityItems);
      if (value) body.parityValue = value;
    }
    const categoryNeedsRatePlan = CATEGORIES_REQUIRING_RATE_PLAN.includes(data.parityCategory);
    if (categoryNeedsRatePlan && data.ratePlanId) body.ratePlanId = data.ratePlanId;
    createMutation.mutate(body);
  };

  const handleSendEmail = useCallback((id: string) => sendEmailMutation.mutate(id), [sendEmailMutation]);

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        Create Parity Notification (Staging Only)
      </Typography>

      <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={3}>
        {/* ── Vendor ID ── */}
        <Controller
          name="vendorId"
          control={control}
          rules={{ required: 'Required' }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Vendor ID"
              required
              disabled={isSubmitting}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              sx={{ maxWidth: 340 }}
              InputProps={{
                endAdornment: vendorOfferSelection.lookupLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
        />

        {/* ── Offer + Rate Plan selectors ── */}
        <VendorOfferSelector
          offers={vendorOfferSelection.offers}
          offersFetching={vendorOfferSelection.offersFetching}
          vendorEnabled={vendorOfferSelection.vendorEnabled}
          selectedOfferId={vendorOfferSelection.selectedOfferId}
          setSelectedOfferId={vendorOfferSelection.setSelectedOfferId}
          selectionRatePlans={needsRatePlan ? vendorOfferSelection.selectionData?.ratePlans : undefined}
          selectionFetching={vendorOfferSelection.selectionFetching}
          setValue={setValue}
          showProperty={false}
          ratePlanIdValue={needsRatePlan ? ratePlanIdValue : undefined}
          disabled={isSubmitting}
        />

        {/* ── Manual ID fields ── */}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Controller
            name="opportunityId"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Opportunity ID"
                required
                disabled={isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 280 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate a random UUID">
                        <Button
                          size="small"
                          variant="text"
                          sx={{ minWidth: 0, fontSize: 11 }}
                          onClick={() => setValue('opportunityId', genSalesforceId('006'))}
                          tabIndex={-1}
                        >
                          Gen
                        </Button>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <Controller
            name="offerId"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Offer ID"
                required
                disabled={isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 280 }}
              />
            )}
          />
          <Controller
            name="parityCategory"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                label="Parity Category"
                required
                disabled={isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 200 }}
              >
                {PARITY_CATEGORIES.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          {needsRatePlan && (
            <Controller
              name="ratePlanId"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Rate Plan ID" disabled={isSubmitting} sx={{ minWidth: 240 }} />
              )}
            />
          )}
        </Stack>

        <Divider />

        {/* ── Parity items (weekly suggestions) ── */}
        <ParityItemsTable
          control={control}
          isSubmitting={isSubmitting}
          fields={fields}
          append={append}
          remove={remove}
        />

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            variant="text"
            disabled={isSubmitting}
            onClick={() => {
              reset();
              vendorOfferSelection.reset();
            }}
          >
            Clear
          </Button>
          <Button variant="contained" type="submit" disabled={isSubmitting}>
            Create Notification
          </Button>
        </Stack>
      </Stack>

      {/* ── Recent notifications table ── */}
      <ParityNotificationsGrid
        notifications={notifications}
        isLoading={notificationsLoading}
        error={notificationsError}
        onSendEmail={handleSendEmail}
        sendingId={sendingEmailId}
      />
    </Box>
  );
}
